import { Button } from '@material-ui/core'
import React from 'react'

import {
  createTrial,
  fetchTrialOptions,
  fetchTrials,
  searchAdmins,
  searchApartmentParents,
  updateTrial
} from 'api/apartment'

import ApartmentApiCrudPage from '../shared/ApartmentApiCrudPage'
import { useSelectedApartment } from '../shared/apartmentStore'

const nameOf = (v) => (v ? v.name : null)

const columns = [
  { name: 'child_name', label: 'Child' },
  { name: 'parent_name', label: 'Parent' },
  { name: 'phone', label: 'Phone', options: { filter: false } },
  { name: 'program', label: 'Subject' },
  { name: 'batchName', label: 'Batch', options: { filter: false } },
  { name: 'trial_date', label: 'Trial Date', options: { filter: false } },
  {
    // Computed from the register on the attendance page — never typed here.
    name: 'attendance',
    label: 'Attendance'
  },
  {
    name: 'followUpOwner',
    label: 'Follow-up Owner',
    options: { filter: false, customBodyRender: nameOf }
  },
  {
    // Who logged the trial — deliberately distinct from the follow-up owner.
    name: 'createdBy',
    label: 'Logged By',
    options: { filter: false, customBodyRender: nameOf }
  },
  {
    // Both are derived: attendance from the register, converted from whether
    // the child went on to enrol in the subject they trialled. Neither is set
    // by hand, so neither appears on the form.
    name: 'converted',
    label: 'Converted',
    options: { customBodyRender: (v) => (v ? 'Yes' : '') }
  }
]

const ApartmentTrials = () => {
  const [apartment] = useSelectedApartment()
  // A child is picked through their parent; the batches a child may trial in
  // depend on that child; and the bookable dates depend on the batch.
  const [children, setChildren] = React.useState([])
  const [initialParent, setInitialParent] = React.useState(null)
  const [batches, setBatches] = React.useState([])
  const [batchId, setBatchId] = React.useState('')

  const fetchParents = React.useCallback(
    (text) => searchApartmentParents(apartment.id, text),
    [apartment.id]
  )

  const loadBatchesFor = React.useCallback(
    async (childId) => {
      setBatches(childId ? await fetchTrialOptions(apartment.id, childId) : [])
    },
    [apartment.id]
  )

  const selectedBatch = batches.find((b) => b.uuid === batchId)
  // Only the dates the chosen batch runs AND still has a trial slot on, today →
  // a month out. The backend rejects anything else, so the dropdown is the same
  // rule, shown — with the remaining slots on each date.
  // `slotsAvailable` is null on an uncapped batch — unlimited, not zero. Saying
  // "null slots left" is how a computed field leaks into a coordinator's face.
  const dateOptions = (selectedBatch?.dates || []).map((d) => ({
    value: d.date,
    label:
      d.slotsAvailable === null || d.slotsAvailable === undefined
        ? `${d.date} · seats open`
        : `${d.date} · ${d.slotsAvailable} seat${
            d.slotsAvailable === 1 ? '' : 's'
          } left`
  }))

  const fields = [
    {
      // Parents and children are main-system users, added by an admin first.
      name: 'parent',
      label: 'Parent (search by name or number)',
      type: 'autocomplete',
      fetchValues: fetchParents,
      initialValue: initialParent,
      clears: ['child', 'batch', 'trial_date'],
      onSelect: (opt) => {
        setChildren(opt?.children || [])
        setBatches([])
        setBatchId('')
      }
    },
    {
      name: 'child',
      label: 'Child (of the selected parent)',
      type: 'select',
      options: children.map((c) => ({ value: c.uuid, label: c.name })),
      // A new child means new eligible batches, so the stale batch and date
      // must not survive the change.
      clears: ['batch', 'trial_date'],
      onSelect: (value) => {
        setBatchId('')
        loadBatchesFor(value)
      }
    },
    {
      // The subject follows from the batch, so it is not asked for.
      name: 'batch',
      label: 'Batch (only the ones this child can join)',
      type: 'select',
      // A trial uses a seat the enrolments have not taken, so the number shown
      // is the batch's free seats. `null` = uncapped, i.e. always room.
      options: batches.map((b) => ({
        value: b.uuid,
        label: `${b.name} · ${
          b.trialSlotsTotal === null || b.trialSlotsTotal === undefined
            ? 'seats open'
            : `${b.trialSlotsTotal} seat${
                b.trialSlotsTotal === 1 ? '' : 's'
              } free`
        }${b.batchType === 'trial' ? ' · trial batch' : ''}`
      })),
      // A different batch runs on different days.
      clears: ['trial_date'],
      onSelect: (value) => setBatchId(value)
    },
    {
      name: 'trial_date',
      label: 'Trial Date (when this batch runs)',
      type: 'select',
      options: dateOptions
    },
    {
      // Writes the admin's User uuid; the API reads back {uuid, name}.
      name: 'follow_up_owner',
      label: 'Follow-up Owner (search by name or number)',
      type: 'autocomplete',
      fetchValues: searchAdmins
    },
    { name: 'follow_up_date', label: 'Follow-up Date', type: 'date' },
    { name: 'price_concerns', label: 'Price concerns', type: 'textarea' },
    { name: 'schedule_concerns', label: 'Schedule concerns', type: 'textarea' },
    {
      name: 'curriculum_feedback',
      label: 'Curriculum feedback',
      type: 'textarea'
    },
    {
      name: 'teacher_feedback',
      label: 'Teacher related feedback',
      type: 'textarea'
    },
    { name: 'notes', label: 'Other notes', type: 'textarea' }
  ]

  const rowToForm = (row) => ({
    ...row,
    parent: row.parentPerson?.uuid || '',
    child: row.childPerson?.uuid || '',
    follow_up_owner: row.followUpOwner?.uuid || ''
  })

  // Opening the modal primes the parent box, its children and the child's
  // batches — doing it in rowToForm would be a setState during render.
  const onModalOpen = React.useCallback(
    async (row) => {
      setInitialParent(row?.parentPerson || null)
      setBatchId(row?.batch || '')
      if (!row?.parentPerson) {
        setChildren([])
        setBatches([])
        return
      }
      const results = await fetchParents(row.parentPerson.name)
      const match = results.find((p) => p.uuid === row.parentPerson.uuid)
      setChildren(match?.children || [])
      await loadBatchesFor(row.childPerson?.uuid)
    },
    [fetchParents, loadBatchesFor]
  )

  // `program` and `attendance` are derived server-side and never sent.
  const formToPayload = (values) => ({
    ...values,
    parent: values.parent || null,
    child: values.child || null,
    batch: values.batch || null,
    follow_up_owner: values.follow_up_owner || null
  })

  const fetchList = React.useCallback(
    () => fetchTrials(apartment.id),
    [apartment.id]
  )
  const createItem = React.useCallback(
    (payload) => createTrial({ ...payload, apartmentSlug: apartment.id }),
    [apartment.id]
  )

  return (
    <ApartmentApiCrudPage
      title="Trials"
      tableTitle="Trial Enquiries"
      entityLabel="Trial"
      fetchList={fetchList}
      createItem={createItem}
      updateItem={updateTrial}
      columns={columns}
      fields={fields}
      rowToForm={rowToForm}
      onModalOpen={onModalOpen}
      formToPayload={formToPayload}
      refreshKey={apartment.id}
      emptyValues={{
        parent: '',
        child: '',
        batch: '',
        trial_date: '',
        follow_up_owner: '',
        follow_up_date: '',
        price_concerns: '',
        schedule_concerns: '',
        curriculum_feedback: '',
        teacher_feedback: '',
        notes: ''
      }}
    />
  )
}

export default ApartmentTrials
